import React from 'react';
import {
  fetchRegionFeatured,
  fetchRegionFeaturedCam,
  fetchRegionMeta, fetchRegionRelated, fetchRegionRelatedNews,
  fetchRegionResortsByStatus, fetchRegionStats,
} from 'api/region/static';
import {
  IRegionCam, IRegionResortsPerStatus, IRegionShort, IRegionStats,
} from 'api/region/types';
import { IRelatedNews, OpenFlag } from 'api/resort/types';
import { AxiosResponse } from 'axios';
import { Hero400Error } from 'components/400';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { NextLinkTr } from 'components/blocks/NextLink';
import { FeaturedCamera } from 'components/common/FeaturedCamera';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { WeatherReports } from 'components/region/WeatherReports';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { RegularAd } from 'googlead/components/RegularAd';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { WEATHER_PAGE_SIZE } from 'ots-constants';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { Routes } from 'routes';

import { isMainLocale } from 'util/i18nFlags';
import { getRegionSlug } from 'util/memcache';
import { useFakeQueryWithInitialValue } from 'util/useQueryWithInitialValue';
import cameraStyles from 'components/common/FeaturedCamera/styles.module.scss';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { useDistanceFormatter } from 'i18n/unitFormat';
import { formatPercentVal } from 'util/formatPercent';

export type RegionSkiReportPageProps = {
  resorts: Partial<IRegionResortsPerStatus>,
  featuredCam: IRegionCam,
  pathInfo: IRegionShort,
  numResorts: number,
  relatedNews: IRelatedNews | null,
  stat: IRegionStats,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-skireport-page';
export const TEMPLATE_ID = '2.1.1';
export const TEMPLATE_NAME = 'Region Snow Report';

const flagsOrder = [OpenFlag.Open, OpenFlag.WeekendsOnly, OpenFlag.TemporarilyClosed, OpenFlag.Closed, OpenFlag.NoReportAvailable, OpenFlag.PermanentlyClosed];

export default function RegionSkiReportPage({
  resorts: preResorts, pathInfo, featuredCam, numResorts,
  relatedNews: preRelatedNews,
  stat: preStat,
}: RegionSkiReportPageProps) {
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');

  /**
   * Refresh data with new one
   */
  const {
    current: resorts,
    hasFreshData: areResortsFresh,
  } = useFakeQueryWithInitialValue(
    ['region-fetchRegionResortsInitial', pathInfo.uuid],
    async () => ({
      [OpenFlag.Open]: await fetchRegionResortsByStatus(pathInfo.uuid, OpenFlag.Open, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.WeekendsOnly]: await fetchRegionResortsByStatus(pathInfo.uuid, OpenFlag.WeekendsOnly, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.Closed]: await fetchRegionResortsByStatus(pathInfo.uuid, OpenFlag.Closed, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.NoReportAvailable]: await fetchRegionResortsByStatus(pathInfo.uuid, OpenFlag.NoReportAvailable, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.TemporarilyClosed]: await fetchRegionResortsByStatus(pathInfo.uuid, OpenFlag.TemporarilyClosed, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.PermanentlyClosed]: await fetchRegionResortsByStatus(pathInfo.uuid, OpenFlag.PermanentlyClosed, 0, WEATHER_PAGE_SIZE),
    } as Partial<IRegionResortsPerStatus>),
    preResorts,
  );

  const {
    current: currentFeaturedCam,
    hasFreshData: hasFreshFeaturedCam,
  } = useFakeQueryWithInitialValue(
    ['region-featuredcam', pathInfo.uuid],
    async () => fetchRegionFeaturedCam(pathInfo.uuid),
    featuredCam,
  );

  const { current: relatedNews, hasFreshData: hasFreshRelatedNews } = useFakeQueryWithInitialValue(
    ['region-relatedNews', pathInfo.uuid],
    async () => fetchRegionRelatedNews(pathInfo.uuid),
    preRelatedNews,
  );

  const { current: stat } = useFakeQueryWithInitialValue(
    ['region-stat', pathInfo.uuid],
    async () => fetchRegionStats(pathInfo.uuid),
    preStat,
  );

  useRegionLayoutBubble({});

  const showLodgingWidget = isMainLocale();

  if (!resorts) {
    return <Hero400Error light />;
  }

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={t('region.skiReport.seoTitle', { region: pathInfo.title })}>
            <NextLinkTr href={Routes.regionCams(pathInfo)}>
              {t('region.skiReport.webcamSnowReport')}
            </NextLinkTr>
          </BlockHeader>
          <BlockContent>
            {m('region.skiReport.seoText', {
              region: pathInfo.title,
              numresorts: numResorts,
              percent_depth_avg: formatPercentVal(stat.depthsPercent.base, ''),
              avgsnowfall: dt(stat.averageSnowfallCm),
            })}
          </BlockContent>
        </BoxWithShadow>
        {flagsOrder
          .filter((flag) => !!resorts[flag]?.data.length)
          .map((flag) => (
            <React.Fragment key={flag}>
              <WeatherReports
                regionId={pathInfo.uuid}
                resorts={resorts[flag]!}
                openFlag={flag}
                isFresh={areResortsFresh}
              />
              <RegularAd />
            </React.Fragment>
          ))}
        {showLodgingWidget && (
          <PlanYourTrip
            region={pathInfo}
            hasSkiRental={false}
            hasCarRental={false}
            hasFlights={false}
            isPartner={false}
          />
        )}
      </Main>
      <Side>
        {currentFeaturedCam && (
          <FeaturedCamera
            header={t('common.featuredCam')}
            cam={currentFeaturedCam.webcam}
            // badge={t('common.featuredSnowreport')}
            // badge={'sdasd'}
            linkBadge={{
              href: Routes.resortCams(currentFeaturedCam.resort),
              title: t('common.seeAllCams'),
            }}
            title={currentFeaturedCam.resort.title}
            href={Routes.resortCams(currentFeaturedCam.resort)}
            isFresh={hasFreshFeaturedCam}
            className={cameraStyles.sideView}
          />
        )}
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh={hasFreshRelatedNews}
        />
        <SidebarAd />
      </Side>
    </>
  );
}

export const fetchProps = async ({ region }:{ region: string }): Promise<RegionSkiReportPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);
  const featuredCam = await fetchRegionFeaturedCam(uuid);

  return {
    type: 'region',
    route: 'skireport',
    resorts: {
      [OpenFlag.Open]: await fetchRegionResortsByStatus(uuid, OpenFlag.Open, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.WeekendsOnly]: await fetchRegionResortsByStatus(uuid, OpenFlag.WeekendsOnly, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.PermanentlyClosed]: await fetchRegionResortsByStatus(uuid, OpenFlag.PermanentlyClosed, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.Closed]: await fetchRegionResortsByStatus(uuid, OpenFlag.Closed, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.NoReportAvailable]: await fetchRegionResortsByStatus(uuid, OpenFlag.NoReportAvailable, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.TemporarilyClosed]: await fetchRegionResortsByStatus(uuid, OpenFlag.TemporarilyClosed, 0, WEATHER_PAGE_SIZE),
    },
    featured,
    featuredCam,
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    relatedNews: await fetchRegionRelatedNews(uuid),
    stat: await fetchRegionStats(uuid),

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.SNOW,
      link: CurrentLink.SNOW_REPORT,
    },
    adTKey: 'metaregion skireport',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.skiReport.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.skireport.description',
      title: 'MetaRegion.skireport.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionSkiReportPageStaticProps = wrapStaticProps(fetchProps);

export const getRegionSkiReportPageServerSideProps = wrapServerSideProps(fetchProps);
